import { useEffect, useState } from 'react';
import { AlertOctagon, MapPin, Check, Phone, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { sosApi } from '../services/api';
import { toast } from 'sonner';

/**
 * Live SOS banner for supervisors and organisation users.
 * Polls every 15s; shows acknowledge button + Google Maps link.
 */
export function SosBanner() {
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    let mounted = true;
    const fetchAlerts = async () => {
      try {
        const res = await sosApi.active();
        if (mounted) setAlerts(res.data || []);
      } catch { /* silent */ }
    };
    fetchAlerts();
    const t = setInterval(fetchAlerts, 15000);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  const ack = async (id) => {
    try {
      await sosApi.acknowledge(id);
      toast.success('SOS acknowledged');
      const res = await sosApi.active();
      setAlerts(res.data || []);
    } catch (err) {
      toast.error('Failed to acknowledge');
    }
  };

  if (alerts.length === 0) return null;

  return (
    <div className="space-y-2 mb-4" data-testid="sos-banner">
      {alerts.map((a) => (
        <div
          key={a.id}
          className="relative overflow-hidden rounded-xl border-2 border-red-400 bg-gradient-to-r from-red-50 via-red-100 to-rose-100 shadow-lg animate-pulse-slow"
          data-testid={`sos-alert-${a.id}`}
        >
          <div className="absolute top-0 left-0 right-0 h-1 bg-red-600 animate-pulse" />
          <div className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className="p-2 bg-red-600 text-white rounded-lg shrink-0 shadow-md">
                <AlertOctagon className="h-6 w-6" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-red-900 text-base">
                  🚨 EMERGENCY SOS — {a.worker_name}
                </p>
                <p className="text-sm text-red-800">
                  {a.client_name && <>Supporting <strong>{a.client_name}</strong> · </>}
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {new Date(a.created_at).toLocaleTimeString('en-AU', { timeZone: 'Australia/Sydney', hour: '2-digit', minute: '2-digit' })}
                  </span>
                </p>
                {a.note && (
                  <p className="text-sm text-slate-800 bg-white/60 rounded px-2 py-1 mt-1 italic">&ldquo;{a.note}&rdquo;</p>
                )}
                {a.worker_email && (
                  <p className="text-xs text-red-700 mt-1 flex items-center gap-1">
                    <Phone className="h-3 w-3" /> {a.worker_email}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {a.maps_url && (
                <a href={a.maps_url} target="_blank" rel="noopener noreferrer" data-testid={`sos-map-${a.id}`}>
                  <Button variant="outline" className="border-red-400 text-red-700 hover:bg-red-50">
                    <MapPin className="h-4 w-4 mr-1" /> View Location
                  </Button>
                </a>
              )}
              <Button
                onClick={() => ack(a.id)}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid={`sos-ack-${a.id}`}
              >
                <Check className="h-4 w-4 mr-1" /> Acknowledge
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
